import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DoneAll as DoneAllIcon, NotificationsNoneOutlined as BellIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTasksMarkAllComplete } from '../../Common/Queries/tasksQueries';

import SimpleLoader from '../Loader/SimpleLoader';
import { showToast } from '../Toast';

const NotificationContainer = styled('div')({
  width: 'min(400px, calc(100vw - 32px))',
  maxWidth: '400px',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '480px',
  backgroundColor: 'var(--hm-surface-raised)',
});

const Header = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
});

const Title = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.95rem',
  color: 'var(--hm-text)',
});

const NotificationList = styled('div')({
  overflowY: 'auto',
  flexGrow: 1,
});

const NotificationLink = styled(Link)({
  display: 'block',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
  },
});

const NotificationItem = styled('div')(({ isRead }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '12px 16px',
  borderBottom: '1px solid var(--hm-border)',
  backgroundColor: isRead ? 'transparent' : 'rgba(47, 143, 131, 0.05)',
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  opacity: isRead ? 0.72 : 1,
  '&:hover': {
    backgroundColor: 'var(--hm-surface-muted) !important',
    opacity: 1,
  },
  '&:last-of-type': {
    borderBottom: 'none',
  },
}));

const DotIndicator = styled('span')({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'var(--hm-primary)',
  display: 'inline-block',
  flexShrink: 0,
  marginTop: '6px',
});

const MessageText = styled(Typography)({
  color: 'var(--hm-text) !important',
  fontSize: '0.86rem !important',
  fontWeight: '500 !important',
  lineHeight: '1.45 !important',
});

const EmptyStateContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 16px',
  textAlign: 'center',
  color: 'var(--hm-text-secondary)',
});

const highlightNotificationMessage = (message) => {
  if (!message) return '';
  const regex = /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+|[A-Z]{2,}(?:\s+[A-Z]{2,})*)/g;
  const parts = message.split(regex);
  let keyCounter = 0;
  return parts.map((part) => {
    keyCounter += 1;
    if (part.match(regex)) {
      return (
        <span key={keyCounter} style={{ fontWeight: 700, color: 'var(--hm-text)' }}>
          {part}
        </span>
      );
    }
    return part;
  });
};

export default function NotificationContent({ notifications, handleClose }) {
  const markAllComplete = useTasksMarkAllComplete();
  const [localTasks, setLocalTasks] = useState([]);

  useEffect(() => {
    if (notifications.data) {
      setLocalTasks(notifications.data);
    }
  }, [notifications.data]);

  const buildLink = (notification) => {
    const { applicationID, interviewID, noteID } = notification;
    const base = `/applications/${applicationID}`;
    const params = new URLSearchParams();

    if (interviewID) params.append('interviewID', interviewID);
    if (noteID) params.append('noteID', noteID);

    return `${base}?${params.toString()}`;
  };

  const handleMarkAllComplete = () => {
    const pendingTasks = localTasks.filter((t) => t.status !== 'COMPLETED');
    if (pendingTasks.length === 0) return;

    // Optimistic UI update
    setLocalTasks((prev) => prev.map((t) => ({ ...t, status: 'COMPLETED' })));

    markAllComplete.mutate(pendingTasks, {
      onSuccess: () => {
        showToast({
          message: 'All notifications marked as completed!',
          severity: 'success',
        });
      },
      onError: () => {
        // Rollback on error
        setLocalTasks(notifications.data || []);
        showToast({
          message: 'Failed to mark notifications as completed.',
          severity: 'error',
        });
      },
    });
  };

  const hasTasks = localTasks.length > 0;
  const hasUnread = localTasks.some((t) => t.status !== 'COMPLETED');

  return (
    <NotificationContainer>
      <Header>
        <Title>Notifications</Title>
        {hasTasks && (
          <Button
            size="small"
            variant="text"
            startIcon={
              markAllComplete.isLoading ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <DoneAllIcon fontSize="small" />
              )
            }
            disabled={!hasUnread || markAllComplete.isLoading}
            onClick={handleMarkAllComplete}
            sx={{
              textTransform: 'none',
              fontSize: '0.78rem',
              fontWeight: 600,
              color: 'var(--hm-primary) !important',
              '&.Mui-disabled': {
                color: 'var(--hm-text-muted) !important',
              },
              '&:hover': {
                backgroundColor: 'var(--hm-primary-soft) !important',
              },
            }}
          >
            {markAllComplete.isLoading ? 'Marking…' : 'Mark all as completed'}
          </Button>
        )}
      </Header>
      <Divider sx={{ borderColor: 'var(--hm-border)' }} />
      <NotificationList>
        {notifications.isLoading && (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <SimpleLoader />
          </Box>
        )}
        {notifications.isSuccess && (
          <>
            {!hasTasks && (
              <EmptyStateContainer>
                <BellIcon
                  sx={{ fontSize: '2.5rem', color: 'var(--hm-text-muted)', mb: 1, opacity: 0.6 }}
                />
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--hm-text)' }}>
                  All caught up!
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: 'var(--hm-text-muted)', mt: 0.5 }}>
                  You have no pending notifications.
                </Typography>
              </EmptyStateContainer>
            )}
            {hasTasks &&
              localTasks.map((notification) => {
                const link = buildLink(notification);
                const isRead = notification.status === 'COMPLETED';
                return (
                  <NotificationLink key={notification.id} to={link} onClick={() => handleClose()}>
                    <NotificationItem isRead={isRead}>
                      {!isRead && <DotIndicator />}
                      <MessageText>
                        {highlightNotificationMessage(notification.message)}
                      </MessageText>
                    </NotificationItem>
                  </NotificationLink>
                );
              })}
          </>
        )}
      </NotificationList>
    </NotificationContainer>
  );
}
